import './MessagesSection.css';

import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  get,
  post,
} from 'aws-amplify/api';
import { uploadData } from 'aws-amplify/storage';

// Icons (these look good)
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
// For components, use individual imports
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

// Custom styled components
const MessageItem = styled(ListItem)(({ theme, sent }) => ({
  flexDirection: 'column',
  alignItems: sent ? 'flex-end' : 'flex-start',
  padding: theme.spacing(1, 2),
}));

const MessageContent = styled(Paper)(({ theme, sent }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(2),
  maxWidth: '80%',
  backgroundColor: sent ? '#28C76F' : theme.palette.grey[100], // Changed to green theme
  color: sent ? theme.palette.primary.contrastText : theme.palette.text.primary,
}));

const MessageTime = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  marginTop: theme.spacing(0.5),
  color: theme.palette.text.secondary,
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#28C76F', // Changed to green theme
    color: '#28C76F',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

// Message types
const MESSAGE_TYPES = {
  GENERAL: 'general',
  AUDIT: 'audit',
  SUPPORT: 'support'
};

const MessagesSection = ({ user }) => {
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('info');
  const [fileToUpload, setFileToUpload] = useState(null);
  const [messageType, setMessageType] = useState(MESSAGE_TYPES.GENERAL);
  const [unreadCounts, setUnreadCounts] = useState({
    [MESSAGE_TYPES.GENERAL]: 0,
    [MESSAGE_TYPES.AUDIT]: 0,
    [MESSAGE_TYPES.SUPPORT]: 0
  });
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      fetchMessages();
      
      // Set up polling for new messages every 30 seconds
      const interval = setInterval(fetchMessages, 30000);
      return () => clearInterval(interval);
    }
  }, [messageType, user]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    if (!user) {
      console.log('No user available, skipping message fetch');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching messages for type:', messageType);
      
      const response = await get({
        apiName: 'UtilityAuditProAPI',
        path: '/messages',
        options: {
          queryParams: {
            userId: user.username,
            type: messageType
          }
        }
      }).response;
      
      if (response.statusCode === 200) {
        const data = await response.body.json();
        console.log('Messages response:', data);
        
        if (data && data.data) {
          setMessages(data.data.messages || []);
          
          // Update unread counts
          setUnreadCounts(prevCounts => ({
            ...prevCounts,
            ...data.data.unreadCounts
          }));
          
          // Mark messages as read
          if (data.data.messages && data.data.messages.length > 0) {
            try {
              await post({
                apiName: 'UtilityAuditProAPI',
                path: '/messages/markAsRead',
                options: {
                  body: {
                    userId: user.username,
                    messageType: messageType
                  }
                }
              }).response;
            } catch (markError) {
              console.error('Error marking messages as read:', markError);
              // Continue anyway - this is not critical
            }
          }
        }
        
        setError(null);
      } else {
        throw new Error(`API returned status code ${response.statusCode}`);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to load messages. Please try again later.');
      setLoading(false);
      
      // Show error alert
      setAlertSeverity('error');
      setAlertMessage('Failed to load messages. Please refresh the page to try again.');
      setAlertOpen(true);
    }
  };

  const handleSendMessage = async () => {
    // Don't send empty messages
    if (!messageText.trim() && !fileToUpload) return;
    
    if (!user) {
      setAlertSeverity('error');
      setAlertMessage('User authentication required. Please log in again.');
      setAlertOpen(true);
      return;
    }
    
    try {
      setSending(true);
      
      // Create optimistic message for UI
      const tempId = `temp-${Date.now()}`;
      const optimisticMessage = {
        id: tempId,
        content: messageText.trim(),
        sender: 'user',
        timestamp: new Date().toISOString(),
        attachmentUrl: null,
        attachmentName: fileToUpload ? fileToUpload.name : null
      };
      
      // Add to message list immediately
      setMessages(prevMessages => [...prevMessages, optimisticMessage]);
      
      // Upload file if present
      let attachmentInfo = null;
      
      if (fileToUpload) {
        try {
          // Check file size (limit to 10MB)
          if (fileToUpload.size > 10 * 1024 * 1024) {
            throw new Error('File size exceeds 10MB limit');
          }
          
          // Upload file to S3
          const timestamp = Date.now();
          const safeFileName = fileToUpload.name.replace(/[^a-zA-Z0-9.-]/g, '_');
          const fileKey = `messages/${user.username}/${timestamp}_${safeFileName}`;
          
          console.log('Uploading file:', fileKey);
          
          await uploadData({
            key: fileKey,
            data: fileToUpload,
            options: {
              contentType: fileToUpload.type,
              metadata: {
                userId: user.username,
                messageType: messageType,
                timestamp: new Date().toISOString()
              }
            }
          });
          
          attachmentInfo = {
            key: fileKey,
            name: fileToUpload.name,
            type: fileToUpload.type,
            size: fileToUpload.size
          };
          
          console.log('File uploaded successfully:', fileKey);
        } catch (fileError) {
          console.error('Error uploading file:', fileError);
          
          // Show error alert but continue without attachment
          setAlertSeverity('warning');
          setAlertMessage(`File upload failed: ${fileError.message}. Message will be sent without attachment.`);
          setAlertOpen(true);
        }
      }
      
      // Send message to API
      console.log('Sending message to API');
      
      const response = await post({
        apiName: 'UtilityAuditProAPI',
        path: '/messages',
        options: {
          body: {
            userId: user.username,
            content: messageText.trim(),
            type: messageType,
            attachment: attachmentInfo
          }
        }
      }).response;
      
      if (response.statusCode === 200 || response.statusCode === 201) {
        console.log('Message sent successfully');
        
        // Clear input fields on success
        setMessageText('');
        setFileToUpload(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // Fetch updated messages from server
        setTimeout(() => {
          fetchMessages();
        }, 500);
      } else {
        throw new Error(`API returned status code ${response.statusCode}`);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      
      // Remove optimistic message if sending failed
      setMessages(prevMessages => 
        prevMessages.filter(msg => !msg.id.startsWith('temp-'))
      );
      
      setAlertSeverity('error');
      setAlertMessage(`Failed to send message: ${err.message}. Please try again later.`);
      setAlertOpen(true);
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      
      // Check file size (limit to 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setAlertSeverity('error');
        setAlertMessage('File size exceeds 10MB limit.');
        setAlertOpen(true);
        return;
      }
      
      setFileToUpload(file);
    }
  };

  const handleRemoveFile = () => {
    setFileToUpload(null);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleTabChange = (event, newValue) => {
    setMessageType(newValue);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };
  
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning">
          User information not available. Please log in again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Alert for notifications */}
      <Collapse in={alertOpen}>
        <Alert
          severity={alertSeverity}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setAlertOpen(false)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{ mb: 2 }}
        >
          {alertMessage}
        </Alert>
      </Collapse>
      
      <Paper sx={{ p: 3, borderRadius: 2, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Messages
        </Typography>
        
        <Typography variant="body1" paragraph>
          Communicate with our team about your utility audits, ask questions, or request support.
        </Typography>
        
        {/* Message categories */}
        <Tabs 
          value={messageType} 
          onChange={handleTabChange} 
          indicatorColor="primary"
          textColor="primary"
          sx={{ mb: 3 }}
        >
          <Tab 
            value={MESSAGE_TYPES.GENERAL} 
            label={
              <Badge 
                color="error" 
                badgeContent={unreadCounts[MESSAGE_TYPES.GENERAL]} 
                max={99}
              >
                General
              </Badge>
            } 
          />
          <Tab 
            value={MESSAGE_TYPES.AUDIT} 
            label={
              <Badge 
                color="error" 
                badgeContent={unreadCounts[MESSAGE_TYPES.AUDIT]} 
                max={99}
              >
                Audit Related
              </Badge>
            } 
          />
          <Tab 
            value={MESSAGE_TYPES.SUPPORT} 
            label={
              <Badge 
                color="error" 
                badgeContent={unreadCounts[MESSAGE_TYPES.SUPPORT]} 
                max={99}
              >
                Support
              </Badge>
            } 
          />
        </Tabs>
      </Paper>
      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card sx={{ height: '60vh', display: 'flex', flexDirection: 'column' }}>
            {/* Messages list */}
            <CardContent 
              sx={{ 
                flexGrow: 1, 
                overflow: 'auto',
                padding: 2,
                '&:last-child': { pb: 2 }
              }}
              className="messages-scroll-container"
            >
              {loading && messages.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress sx={{ color: '#28C76F' }} /> {/* Changed to green theme */}
                </Box>
              ) : messages.length === 0 ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '100%' 
                }}
                className="empty-messages"
                >
                  <EmailIcon sx={{ fontSize: 60, mb: 2, color: 'text.secondary' }} className="empty-messages-icon" />
                  <Typography variant="h6" color="textSecondary">No messages yet</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Start the conversation by sending a message below.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {messages.map((message, index) => {
                    const isUserMessage = message.sender === 'user';
                    return (
                      <MessageItem 
                        key={message.id || index} 
                        sent={isUserMessage} 
                        className={isUserMessage ? 'message-item-container sent-container' : 'message-item-container received-container'}
                      >
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'flex-start',
                          width: '100%', 
                          justifyContent: isUserMessage ? 'flex-end' : 'flex-start'
                        }}>
                          {!isUserMessage && (
                            <StyledBadge
                              overlap="circular"
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              variant="dot"
                              sx={{ mr: 1 }}
                              className="status-badge"
                            >
                              <Avatar alt="Support" src="/static/images/support-avatar.png" />
                            </StyledBadge>
                          )}
                          
                          <Box sx={{ maxWidth: '80%' }}>
                            <MessageContent 
                              sent={isUserMessage} 
                              className={isUserMessage ? 'message-content-custom sent-message' : 'message-content-custom received-message'}
                            >
                              <Typography variant="body1">{message.content}</Typography>
                              
                              {message.attachmentUrl && (
                                <Box sx={{ mt: 1 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    href={message.attachmentUrl}
                                    target="_blank"
                                    download
                                    sx={{ 
                                      textTransform: 'none',
                                      color: isUserMessage ? 'white' : 'inherit',
                                      borderColor: isUserMessage ? 'white' : 'inherit'
                                    }}
                                  >
                                    {message.attachmentName || 'Download attachment'}
                                  </Button>
                                </Box>
                              )}
                            </MessageContent>
                            
                            <MessageTime className="message-time-custom">
                              {formatTimestamp(message.timestamp)}
                            </MessageTime>
                          </Box>
                          
                          {isUserMessage && (
                            <Avatar 
                              sx={{ ml: 1 }} 
                              alt="User"
                              // Use initials if available, otherwise a default
                              src="/static/images/user-avatar.png"
                            />
                          )}
                        </Box>
                      </MessageItem>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </List>
              )}
            </CardContent>
            
            <Divider />
            
            {/* Message input */}
            <Box sx={{ p: 2 }}>
              {fileToUpload && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 1,
                  p: 1,
                  borderRadius: 1,
                  backgroundColor: (theme) => theme.palette.grey[100]
                }}
                className="file-attachment"
                >
                  <AttachFileIcon color="primary" sx={{ mr: 1 }} className="file-attachment-icon" />
                  <Typography variant="body2" noWrap sx={{ flex: 1 }} className="file-attachment-name">
                    {fileToUpload.name}
                  </Typography>
                  <IconButton size="small" onClick={handleRemoveFile} className="file-attachment-remove">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              
              <Grid container spacing={1} alignItems="flex-end">
                <Grid item xs>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    variant="outlined"
                    placeholder="Type your message..."
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={sending}
                  />
                </Grid>
                
                <Grid item>
                  <input
                    accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    id="icon-button-file"
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                    ref={fileInputRef}
                  />
                  <label htmlFor="icon-button-file">
                    <IconButton 
                      color="primary" 
                      component="span"
                      disabled={sending || fileToUpload !== null}
                      className="attach-button"
                    >
                      <AttachFileIcon />
                    </IconButton>
                  </label>
                </Grid>
                
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon className="send-icon" />}
                    onClick={handleSendMessage}
                    disabled={sending || (!messageText.trim() && !fileToUpload)}
                  >
                    Send
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default MessagesSection;